import type { Profile } from './profiles';

/**
 * Seeing Machines profile for automotive software engineers
 */
export const seeingMachinesProfile: Profile = {
  name: 'Custom for Seeing Machines',
  averageSwitchingFrequencySeconds: 300,
  minMeetingsPerDay: 1,
  meetings: [
    {
      title: 'Daily Standup',
      durationMinutes: 30,
      entries: [
        { tool: 'microsoft_teams', title: 'Engineering Team Standup' },
        { tool: 'jira', title: 'Current Sprint Board' },
        { tool: 'slack', title: 'standup-updates' },
      ],
      aiNotes: {
        concise: 'Daily team sync on automotive software development progress.',
        detailed:
          'Participating in daily standup meeting via Microsoft Teams, reviewing current sprint tasks in Jira, sharing updates and blockers with the team through Slack channels.',
      },
    },
    {
      title: 'DASH-2847: Dashboard UI Code Review',
      durationMinutes: 60,
      entries: [
        { tool: 'github', title: 'PR #892: Dashboard touch input optimization' },
        { tool: 'visual_studio_code', title: 'dashboard/TouchInputHandler.cpp' },
        { tool: 'jira', title: 'DASH-2847: Optimize dashboard touch responsiveness' },
        { tool: 'slack', title: 'code-reviews' },
      ],
      aiNotes: {
        concise: 'Reviewing dashboard touch input optimization implementation.',
        detailed:
          'Conducting code review for dashboard touch input optimization PR on GitHub, examining C++ implementation in VS Code, tracking progress in Jira, and discussing review feedback with team via Slack.',
      },
    },
    {
      title: 'Sprint Planning - Q1 2025',
      durationMinutes: 90,
      entries: [
        { tool: 'jira', title: 'Q1 2025 Sprint Backlog' },
        { tool: 'microsoft_teams', title: 'Engineering Sprint Planning' },
        { tool: 'microsoft_sharepoint', title: 'Q1 Roadmap Documentation' },
        { tool: 'slack', title: 'sprint-planning' },
      ],
      aiNotes: {
        concise: 'Planning Q1 sprint tasks for automotive dashboard features.',
        detailed:
          'Participating in Q1 sprint planning session via Teams, reviewing and prioritizing backlog items in Jira, referencing roadmap documentation on SharePoint, and coordinating with team through Slack.',
      },
    },
    {
      title: '1:1 with Engineering Lead',
      durationMinutes: 30,
      entries: [
        { tool: 'microsoft_teams', title: '1:1 with Sarah Chen' },
        { tool: 'microsoft_outlook', title: 'Career Development Discussion Notes' },
        { tool: 'slack', title: 'Sarah Chen (DM)' },
      ],
      aiNotes: {
        concise: 'Career development and project discussion with engineering lead.',
        detailed:
          'Meeting with engineering lead via Microsoft Teams to discuss professional growth, documenting action items in Outlook, and following up on discussion points through Slack direct messages.',
      },
    },
    {
      title: 'System Integration Review',
      durationMinutes: 60,
      entries: [
        { tool: 'microsoft_teams', title: 'Integration Team Meeting' },
        { tool: 'github', title: 'automotive-platform/integration-tests' },
        { tool: 'jira', title: 'System Integration - Sprint 47' },
        { tool: 'microsoft_sharepoint', title: 'Integration Test Documentation' },
      ],
      aiNotes: {
        concise: 'Reviewing automotive system integration testing and results.',
        detailed:
          'Meeting to review automotive platform integration testing, examining test results on GitHub, tracking integration tasks in Jira, and updating test documentation on SharePoint.',
      },
    },
  ],
  sessions: [
    {
      name: 'Feature Development - Dashboard Graphics',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'dashboard/GraphicsRenderer.cpp' },
        { tool: 'visual_studio_code', title: 'dashboard/UIComponents.h' },
        { tool: 'visual_studio_code', title: 'dashboard/TouchInputHandler.cpp' },
        { tool: 'visual_studio_code', title: 'tests/DashboardTests.cpp' },
        { tool: 'iTerm2', title: 'cmake --build . && ctest' },
        { tool: 'github', title: 'PR #892: Dashboard touch input optimization' },
        { tool: 'jira', title: 'DASH-2847: Optimize dashboard touch responsiveness' },
        { tool: 'slack', title: 'engineering' },
        { url: 'https://stackoverflow.com', title: 'C++ graphics optimization techniques' },
        { url: 'https://github.com', title: 'Automotive UI best practices' },
      ],
      aiNotes: {
        concise: 'Developing dashboard graphics and touch input features.',
        detailed:
          'Working on automotive dashboard graphics renderer and touch input optimization in VS Code, implementing UI components in C++, running tests with CMake and CTest, reviewing related GitHub PRs, and tracking progress in Jira.',
      },
    },
    {
      name: 'Bug Fix - Screen Detection Issue',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'screen/ScreenDetection.cpp' },
        { tool: 'visual_studio_code', title: 'screen/DisplayManager.cpp' },
        { tool: 'visual_studio_code', title: 'diagnostics/SystemLogs.cpp' },
        { tool: 'iTerm2', title: 'git diff screen/' },
        { tool: 'iTerm2', title: './run_diagnostics.sh' },
        { tool: 'github', title: 'Issue #734: Screen detection fails on startup' },
        { tool: 'jira', title: 'DASH-2901: Fix screen detection initialization' },
        { tool: 'slack', title: 'bug-reports' },
        { url: 'https://docs.automotive.org', title: 'Display detection protocols' },
      ],
      aiNotes: {
        concise: 'Fixing screen detection initialization bug in automotive system.',
        detailed:
          'Debugging and fixing screen detection initialization issues in VS Code, examining display manager and system logs, running diagnostic scripts, reviewing GitHub issues, and coordinating bug fix through Jira and Slack.',
      },
    },
    {
      name: 'Code Review',
      predominantTool: 'github',
      entries: [
        { tool: 'github', title: 'PR #887: Add night mode theme' },
        { tool: 'github', title: 'PR #895: Implement driver monitoring alerts' },
        { tool: 'github', title: 'PR #902: Update sensor calibration logic' },
        { tool: 'visual_studio_code', title: 'themes/NightMode.cpp' },
        { tool: 'visual_studio_code', title: 'monitoring/AlertSystem.cpp' },
        { tool: 'jira', title: 'In Review - Current Sprint' },
        { tool: 'slack', title: 'code-reviews' },
        { url: 'https://github.com', title: 'Code review best practices' },
      ],
      aiNotes: {
        concise: 'Reviewing PRs for night mode, driver monitoring, and sensor calibration.',
        detailed:
          'Performing code reviews on GitHub for multiple automotive features including night mode theme, driver monitoring alerts, and sensor calibration updates. Examining implementation code in VS Code and coordinating feedback through Jira and Slack.',
      },
    },
    {
      name: 'Documentation Review',
      predominantTool: 'microsoft_sharepoint',
      entries: [
        { tool: 'microsoft_sharepoint', title: 'System Architecture Documentation' },
        { tool: 'microsoft_sharepoint', title: 'Dashboard API Reference' },
        { tool: 'microsoft_sharepoint', title: 'Integration Testing Guide' },
        { tool: 'microsoft_word', title: 'Technical Specification - Driver Monitoring.docx' },
        { tool: 'visual_studio_code', title: 'README.md' },
        { tool: 'visual_studio_code', title: 'docs/ARCHITECTURE.md' },
        { tool: 'jira', title: 'DASH-2823: Update technical documentation' },
        { tool: 'slack', title: 'documentation' },
      ],
      aiNotes: {
        concise: 'Reviewing and updating technical documentation for dashboard system.',
        detailed:
          'Reviewing system architecture documentation on SharePoint, updating API reference and testing guides, editing technical specifications in Word, updating markdown documentation in VS Code, tracking documentation tasks in Jira, and coordinating with team via Slack.',
      },
    },
    {
      name: 'Project Planning',
      predominantTool: 'jira',
      entries: [
        { tool: 'jira', title: 'Q1 2025 Roadmap - Dashboard Features' },
        { tool: 'jira', title: 'DASH-2950: Implement multi-screen support' },
        { tool: 'jira', title: 'DASH-2978: Enhance voice control integration' },
        { tool: 'microsoft_excel', title: 'Resource Allocation - Q1 2025.xlsx' },
        { tool: 'microsoft_sharepoint', title: 'Feature Specifications Q1' },
        { tool: 'microsoft_teams', title: 'Product Planning Discussion' },
        { tool: 'slack', title: 'product-engineering' },
        { url: 'https://miro.com', title: 'Q1 feature roadmap board' },
      ],
      aiNotes: {
        concise: 'Planning Q1 dashboard features including multi-screen and voice control.',
        detailed:
          'Planning Q1 roadmap for dashboard features in Jira, creating tasks for multi-screen support and voice control enhancements, reviewing resource allocation spreadsheet in Excel, checking feature specifications on SharePoint, discussing with product team via Teams, and coordinating through Slack.',
      },
    },
    {
      name: 'Team Communication',
      predominantTool: 'slack',
      maxLengthMinutes: 25,
      entries: [
        { tool: 'slack', title: 'engineering' },
        { tool: 'slack', title: 'dashboard-team' },
        { tool: 'slack', title: 'Michael Chen (DM)' },
        { tool: 'microsoft_teams', title: 'Engineering Team Chat' },
        { tool: 'microsoft_outlook', title: 'Email: Team Updates' },
        { tool: 'jira', title: 'Recent Activity Feed' },
      ],
      aiNotes: {
        concise: 'Team communication and collaboration on engineering tasks.',
        detailed:
          'Communicating with engineering team through Slack channels, coordinating with dashboard team members, exchanging direct messages, participating in Teams chat, checking email updates in Outlook, and monitoring recent activity in Jira.',
      },
    },
    {
      name: 'NetSuite Expense Reporting',
      predominantTool: 'netsuite',
      maxLengthMinutes: 30,
      entries: [
        { tool: 'netsuite', title: 'Expense Report - November 2025' },
        { tool: 'netsuite', title: 'Purchase Orders Dashboard' },
        { tool: 'microsoft_outlook', title: 'Email: Receipt - Development Tools' },
        { tool: 'microsoft_outlook', title: 'Email: Receipt - Conference Registration' },
        { tool: 'slack', title: 'finance' },
      ],
      aiNotes: {
        concise: 'Submitting expense reports and managing purchase orders.',
        detailed:
          'Managing expense reports in NetSuite for November 2025, reviewing purchase orders dashboard, gathering receipts from Outlook email, and coordinating with finance team through Slack.',
      },
    },
    {
      name: 'Email Management',
      predominantTool: 'microsoft_outlook',
      maxLengthMinutes: 30,
      entries: [
        { tool: 'microsoft_outlook', title: 'Inbox - Engineering Updates' },
        { tool: 'microsoft_outlook', title: 'Engineering Team Mailing List' },
        { tool: 'microsoft_outlook', title: 'Calendar - Upcoming Meetings' },
        { tool: 'microsoft_teams', title: 'Team Chat Notifications' },
        { tool: 'slack', title: 'Unread messages' },
        { tool: 'jira', title: 'My Open Issues' },
      ],
      aiNotes: {
        concise: 'Managing emails and coordinating daily communications.',
        detailed:
          'Processing engineering updates in Outlook inbox, reviewing team mailing list messages, checking calendar for upcoming meetings, responding to Teams notifications, catching up on Slack messages, and reviewing open Jira issues.',
      },
    },
    {
      name: 'System Testing',
      predominantTool: 'iTerm2',
      entries: [
        { tool: 'iTerm2', title: './run_integration_tests.sh' },
        { tool: 'iTerm2', title: 'cmake --build . --target test' },
        { tool: 'visual_studio_code', title: 'tests/IntegrationTests.cpp' },
        { tool: 'visual_studio_code', title: 'tests/SystemTests.cpp' },
        { tool: 'github', title: 'CI/CD Pipeline Status' },
        { tool: 'jira', title: 'DASH-2890: Update integration test suite' },
        { tool: 'slack', title: 'testing' },
      ],
      aiNotes: {
        concise: 'Running system and integration tests for automotive platform.',
        detailed:
          'Running integration and system tests via terminal, building test targets with CMake, examining test implementation in VS Code, monitoring CI/CD pipeline on GitHub, tracking test improvements in Jira, and coordinating with testing team on Slack.',
      },
    },
    {
      name: 'Technical Research',
      predominantTool: 'visual_studio_code',
      maxLengthMinutes: 35,
      entries: [
        { tool: 'visual_studio_code', title: 'research/GraphicsOptimization.md' },
        { tool: 'visual_studio_code', title: 'prototypes/TouchGestures.cpp' },
        { url: 'https://github.com', title: 'Automotive graphics libraries' },
        { url: 'https://stackoverflow.com', title: 'Real-time rendering optimization' },
        { url: 'https://docs.automotive.org', title: 'Safety standards documentation' },
        { tool: 'notion', title: 'Research Notes - Q4 2024' },
        { tool: 'slack', title: 'research' },
      ],
      aiNotes: {
        concise: 'Researching graphics optimization and touch gesture technologies.',
        detailed:
          'Researching graphics optimization techniques and prototyping touch gesture implementations in VS Code, exploring automotive graphics libraries on GitHub, studying real-time rendering best practices, reviewing safety standards, documenting findings in Notion, and discussing with research team on Slack.',
      },
    },
    {
      name: 'Performance Analysis',
      predominantTool: 'microsoft_excel',
      maxLengthMinutes: 30,
      entries: [
        { tool: 'microsoft_excel', title: 'Dashboard Performance Metrics - Q4 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Benchmark Comparison.xlsx' },
        { tool: 'microsoft_word', title: 'Performance Analysis Report.docx' },
        { tool: 'jira', title: 'DASH-2945: Performance optimization initiative' },
        { tool: 'slack', title: 'performance' },
      ],
      aiNotes: {
        concise: 'Analyzing dashboard performance metrics and creating reports.',
        detailed:
          'Analyzing performance metrics in Excel spreadsheets, comparing benchmark data, documenting findings in Word report, tracking performance optimization tasks in Jira, and discussing results with performance team on Slack.',
      },
    },
  ],
};
